import debounce from 'lodash.debounce';

import {
  DISMISS_GLOBAL_BANNER,
  MINIFY_HF,
  RECEIVE_REMOTE_HF_CONTENT,
  RESET_STATE,
  SET_DUAL_GLOBAL_BANNERS_DATA,
  SET_FEDERATED_LOGIN_MODAL_VISIBILITY,
  SET_FOOTER_HISTORY_RECOS,
  SET_GLOBAL_BANNER_DATA,
  SET_HF_DATA,
  SET_HF_FOOT_SUBSCRIBE_ERROR,
  SET_HF_FOOT_SUBSCRIBE_SUBMIT,
  SET_HF_FOOT_SUBSCRIBE_SUBMITTING,
  SET_HF_MOBILE_BREAKPOINT,
  SET_HF_MOBILE_DATA,
  SET_HF_SEARCH_SUGGEST,
  SET_HF_SEARCH_SUGGEST_ACTIVE_INDEX,
  SET_HF_SEARCH_TERM,
  SET_HF_SEARCH_USER_TERMS,
  SET_HF_SEARCH_ZERO_STATE,
  SET_HF_SELECTED_TAB,
  SET_HF_SIGN_UP_MODAL,
  SET_HF_TOPBANNER_DATA,
  SET_MISSING_FOOTER_SLOTS_DATA,
  SET_REWARDS_BENEFITS,
  SET_ZAW_CONTENT_SYMPHONY_HEADER_DATA,
  SHOW_HF_TOP_BANNER,
  TOGGLE_HF,
  TOGGLE_MOBILE_HEADER_EXPAND,
  UPDATE_ORIGINAL_TERM
} from 'constants/reduxActions';
import { EXPIRE_IMMEDIATE_COOKIE_TIME, NEWLY_REGISTERED_COOKIE, OPAL_PROFILE_COOKIE } from 'constants/cookies';
import { HF_TOPBANNER_CONTENT_TYPE_REWARDS } from 'constants/rewardsInfo';
import { DISMISSED_GLOBAL_BANNER_SESSION_STORAGE_KEY } from 'constants/appConstants';
import { FEATURE_VIP_DISABLED } from 'constants/features';
import { trackError } from 'helpers/ErrorUtils';
import { fetchErrorMiddleware, fetchErrorMiddlewareMaybeJson } from 'middleware/fetchErrorMiddleware';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import { evSearchAutoCompleteImpression } from 'events/headerFooter';
import { trackOperation } from 'events/ops/opsEvent';
import { loadFromLocalStorage, saveToLocalStorage } from 'helpers/localStorageUtilities';
import { isForceRefreshed } from 'helpers/ClientUtils';
import {
  fetchLoyaltyMembershipBalanceV3,
  fetchLoyaltyMembershipV3,
  fetchRewardsInfoV2,
  isRewardsDetermined,
  receiveLoyaltyMembershipBalance
} from 'actions/account/rewards';
import { isMobileContentBreakpoint } from 'helpers/HtmlHelpers';
import { loadProductDetailPage } from 'actions/productDetail';
import { setAndStoreCookie } from 'actions/session';
import { PRODUCT_ASIN } from 'common/regex';
import { stripSpecialCharsConsolidateWhitespace } from 'helpers';
import { cartCount, enrollRewardsV2, subscribeToMarketingListZSub } from 'apis/mafia';
import { recommendationsSearch } from 'apis/janus';
import { getSymphonySlots } from 'apis/zcs';
import { buildBannerPtcHeaders, buildPtcBasicHeaders, isBannerPtcCatalogEnabled } from 'helpers/ptcHeaders';
import {
  ZCS_PTC_BUSINESS_UNIT_HEADER,
  ZCS_PTC_CATEGORY1_HEADER,
  ZCS_PTC_PLATFORM_HEADER,
  ZCS_PTC_REFERRER_HEADER,
  ZCS_PTC_REFERRER_MAX_LENGTH
} from 'constants/ptc';
import { profile, variableBias } from 'apis/opal';
import { searchAutoComplete } from 'apis/calypso';
import { changeCartCount, requestCartCount } from 'actions/cart';
import ProductUtils from 'helpers/ProductUtils';
import marketplace from 'cfg/marketplace.json';
import { PAGE_TYPE_BANNERS } from 'utils/constants';
import { updateUserSearchTermsFromLocalStorage } from 'helpers/searchInputUtils';
import { getAllFilter, strip } from 'helpers/SearchUtils';
import { preloadDualGlobalBannerImages } from 'helpers/globalBannerImagePreload';
import { areDualGlobalBannersRenderEquivalent } from 'helpers/globalBannerRenderComparison';
import { clearPromoBalanceSessionStorage } from 'helpers/CheckoutUtils';
import { resolveDefaultTab } from 'utils/variableBias';
import { selectCalypsoConfig, selectJanusConfig, selectMafiaConfig, selectOpalApi, selectZcsConfig } from 'selectors/environment';
import { selectUnleashToggle } from 'selectors/features';

const {
  domain,
  search: { autoCompleteCategories },
  features: { showSigninIncentive }
} = marketplace;

const SEARCH_SUGGEST_DEBOUNCE = 200;
const SEGMENTED_GLOBAL_BANNER_ENDPOINT = '/marty/api/segmented-global-banner';

// Per-store refetch state retains completed fields for dedupe and cancelled updates for retry on remount.
const segmentedBannerRequests = new WeakMap();

export function resetState() {
  return {
    type: RESET_STATE
  };
}

export function updateOriginalTerm(originalTerm = '') {
  return {
    type: UPDATE_ORIGINAL_TERM,
    originalTerm
  };
}

export function searchByTerm(
  { term, facet, filter },
  router,
  { forceRefreshTypedSearch, asinFetcher } = {
    forceRefreshTypedSearch: isForceRefreshed(marketplace, 'typedSearch'),
    asinFetcher: loadProductDetailPage
  }
) {
  return dispatch => {
    function typedSearchPush(url, forcedRouterMethod) {
      if (!router) {
        // For when remotely consumed HFs can't client route
        window.location = url;
      } else if (forcedRouterMethod && router[forcedRouterMethod]) {
        router[forcedRouterMethod](url);
      } else if (forceRefreshTypedSearch) {
        router.forceRefreshPush(url);
      } else {
        router.push(url);
      }
    }

    setHFSearchSuggestionsActiveIndex(null);
    const possibleAsin = term.toUpperCase().trim();
    const encodedTerm = encodeURIComponent(term);
    // If searching by ASIN
    if (possibleAsin.match(PRODUCT_ASIN)) {
      return dispatch(asinFetcher({ asin: possibleAsin }, { errorOnOos: false, background: true }))
        .then(product => {
          const productUrl = ProductUtils.getProductUrlFromAsin(product, possibleAsin);
          if (productUrl) {
            // going to a product
            typedSearchPush(productUrl, 'pushPreserveAppRoot');
          } else {
            typedSearchPush(`/search?term=${encodedTerm}`);
          }
        })
        .catch(() => {
          // If the asin lookup fails, there is not much we can do except to just perform a search
          typedSearchPush(`/search?term=${encodedTerm}`);
        });
    } else {
      if (facet) {
        typedSearchPush(`/search/${encodedTerm}/filter/${facet}`);
        // if a filter and != 'All'
      } else if (filter && filter.value !== getAllFilter().value) {
        const strippedTerm = strip(term);
        const { value } = filter;
        typedSearchPush(`/search/${strippedTerm === '' ? null : strippedTerm}/${value}`);
      } else {
        typedSearchPush(`/search?term=${encodedTerm}`);
      }
    }
    return Promise.resolve();
  };
}

export function fetchRewardsInfoForTopBanner() {
  return (dispatch, getState) => {
    const appState = getState();
    const { customer: isRecognizedCustomer } = appState;
    const isVipDisabled = selectUnleashToggle(FEATURE_VIP_DISABLED, appState)?.enabled;

    if (!isRecognizedCustomer) {
      dispatch(isRewardsDetermined(true));
    }

    if (isRecognizedCustomer) {
      const rewardsInfoRequest = dispatch(fetchRewardsInfoV2({ updateTopBannerData: true }));
      if (isVipDisabled) {
        dispatch(receiveLoyaltyMembershipBalance(null));
        const loyaltyMembershipBalanceRequest = dispatch(fetchLoyaltyMembershipBalanceV3());
        const loyaltyMembershipRequest = dispatch(fetchLoyaltyMembershipV3());
        return Promise.all([rewardsInfoRequest, loyaltyMembershipBalanceRequest, loyaltyMembershipRequest]);
      }
      return rewardsInfoRequest;
      // Don't show the top banner until we get the rewards info from the server.
    } else if (showSigninIncentive) {
      // User is not recognized.  Let's mark the rewards content as empty, so that
      // the reducer picks a generic top banner phrase instead (e.g. 'You are awesome!' or 'You should log in!')
      dispatch(showHFTopBanner());
      dispatch(
        setHFTopBannerContent({
          bannerType: HF_TOPBANNER_CONTENT_TYPE_REWARDS,
          bannerContent: {},
          showSigninIncentive
        })
      );
    }
  };
}

export function getCartCount(getCall = cartCount) {
  return (dispatch, getState) => {
    const state = getState();
    if (!state.cart.isCartCountLoading) {
      const { cookies } = state;
      const mafiaConfig = selectMafiaConfig(state);
      dispatch(requestCartCount()); // tell the reducer that we are loading to debounce
      return getCall(mafiaConfig, cookies)
        .then(fetchErrorMiddleware)
        .then(data => dispatch(changeCartCount(data?.totalCartItems || 0)))
        .catch(err => {
          trackError('NON-FATAL', 'Could not retrieve cart count', err);
          dispatch(changeCartCount(0));
        });
    }
  };
}

// Stores the missing footer slots data from the ZCS response from the HF cache to emit missing-data ops metrics.
export function setMissingFooterSlots(missingFooterSlots) {
  return {
    type: SET_MISSING_FOOTER_SLOTS_DATA,
    missingFooterSlots
  };
}

export function setHFContent(data) {
  return {
    type: SET_HF_DATA,
    data
  };
}

export function setHFMobileContent(data) {
  return {
    type: SET_HF_MOBILE_DATA,
    data
  };
}

let mobileHFPromise = null;

export function resetMobileHFPromise() {
  mobileHFPromise = null;
}

export function fetchMobileHFContent() {
  return (dispatch, getState) => {
    const {
      headerFooter: { mobileContent }
    } = getState();
    if (mobileContent) {
      return;
    }
    if (mobileHFPromise) {
      return mobileHFPromise;
    }

    const { url } = selectZcsConfig(getState());
    const {
      siteId,
      subsiteId: { mobile: mobileSubsiteId }
    } = marketplace;
    const endpoint = `${url}/zcs/headerfooter?siteId=${siteId}&subsiteId=${mobileSubsiteId}&mobile=true`;

    mobileHFPromise = fetch(endpoint)
      .then(fetchErrorMiddleware)
      .then(data => dispatch(setHFMobileContent(data)))
      .catch(err => {
        mobileHFPromise = null;
        trackError('NON-FATAL', 'Could not fetch mobile HF content.', err);
      });

    return mobileHFPromise;
  };
}

export function setHFSelectedTab(tab) {
  return {
    type: SET_HF_SELECTED_TAB,
    tab
  };
}

export function fetchVariableBiasTab() {
  return (dispatch, getState) => {
    const state = getState();
    const {
      cookies,
      customer: isRecognizedCustomer,
      headerFooter: { selectedTab }
    } = state;

    if (!isRecognizedCustomer || selectedTab) {
      return;
    }

    const opalApi = selectOpalApi(state);

    return variableBias(opalApi, cookies)
      .then(fetchErrorMiddleware)
      .then(data => {
        const tab = resolveDefaultTab(data);
        dispatch(setHFSelectedTab(tab));
      })
      .catch(err => trackError('NON-FATAL', 'Could not load variable bias for nav tab.', err));
  };
}

export function setBenefits(data) {
  return {
    type: SET_REWARDS_BENEFITS,
    benefits: data
  };
}

export function setHFTopBannerContent({ bannerType, bannerContent, showSigninIncentive }) {
  return {
    type: SET_HF_TOPBANNER_DATA,
    bannerType,
    bannerContent,
    showSigninIncentive
  };
}

export function showHFTopBanner() {
  return {
    type: SHOW_HF_TOP_BANNER
  };
}

export function setHFBreakpoint(isMobile) {
  return {
    type: SET_HF_MOBILE_BREAKPOINT,
    isMobile
  };
}

export function setHeaderFooterVisibility(isHfVisible) {
  return {
    type: TOGGLE_HF,
    isHfVisible
  };
}

export function setMinifiedHeaderFooter(isHfMinified) {
  return {
    type: MINIFY_HF,
    isHfMinified
  };
}

export function setRemoteHFContent(remoteHfContent) {
  return {
    type: RECEIVE_REMOTE_HF_CONTENT,
    remoteHf: remoteHfContent
  };
}

export function checkForHFBreakpoint(checkBreakpoint = isMobileContentBreakpoint) {
  return (dispatch, getState) => {
    const {
      headerFooter: { isMobile }
    } = getState();
    const header = document.querySelector('[data-header-container]');
    const currentIsMobile = header ? checkBreakpoint(header) : false;
    if (isMobile !== currentIsMobile) {
      dispatch(setHFBreakpoint(currentIsMobile));
    }
  };
}

// Main search term & autocomplete shtufff
export function setHFSearchTerm(term) {
  return {
    type: SET_HF_SEARCH_TERM,
    term
  };
}

export function resetHFSearchSuggestions() {
  return dispatch => {
    dispatch(setHFSearchSuggestionsActiveIndex());
    dispatch(setHFSearchSuggestions());
  };
}

export function loadHFSearchUserTerms(term = '') {
  return {
    type: SET_HF_SEARCH_USER_TERMS,
    userSearchTerms: updateUserSearchTermsFromLocalStorage(term) ?? []
  };
}

export function setHFSearchZeroState(isZeroState = true) {
  return {
    type: SET_HF_SEARCH_ZERO_STATE,
    isZeroState
  };
}

export function setHFSearchSuggestions(suggestions = []) {
  return {
    type: SET_HF_SEARCH_SUGGEST,
    suggestions
  };
}

export function setHFSearchSuggestionsActiveIndex(suggestionIndex = null) {
  return {
    type: SET_HF_SEARCH_SUGGEST_ACTIVE_INDEX,
    suggestionIndex
  };
}

/**
 * Call for Search Sugesstions
 * @param {object} calypso          calypso config
 * @param {string} term             term to autocomplete on
 * @param  {bool}   categories      show/hide categories
 * @param autoCompleteFetch                   api fetcher  function
 * @return {object}                           promise
 */
export function autoComplete(calypso, state, term, categories, autoCompleteFetch = searchAutoComplete) {
  return autoCompleteFetch(calypso, state, { term, categories }).then(fetchErrorMiddleware);
}

// Stole this debounce setup from https://gist.github.com/krstffr/245fe83885b597aabaf06348220c2fe9
export const callSearchSuggest = debounce(
  (dispatch, state, calypso, term, autoComp = autoComplete) =>
    autoComp(calypso, state, stripSpecialCharsConsolidateWhitespace(term), autoCompleteCategories)
      .then(data => {
        const suggestions = data.matches || data;
        if (suggestions) {
          dispatch(setHFSearchSuggestions(suggestions));
        } else {
          dispatch(resetHFSearchSuggestions());
        }
        track(() => [
          evSearchAutoCompleteImpression,
          {
            termSearched: term,
            resultCount: suggestions ? suggestions.length : 0,
            results: suggestions ? suggestions.map(({ suggestion }, row) => ({ result: suggestion, row })) : []
          }
        ]);
        trackOperation('SearchAutoComplete', 'success');
      })
      .catch(err => {
        trackError('NON-FATAL', `Could not call search suggestions for term: ${term}`, err);
        trackOperation('SearchAutoComplete', 'failure');
      }),
  SEARCH_SUGGEST_DEBOUNCE
);

export function handleHFSearchChange(e, autoComp = autoComplete) {
  const term = e.target.value;
  return (dispatch, getState) => {
    const appState = getState();

    dispatch(setHFSearchSuggestionsActiveIndex(null));
    dispatch(setHFSearchTerm(term));

    if (term) {
      const calypso = selectCalypsoConfig(appState);
      dispatch(resetHFSearchSuggestions());
      callSearchSuggest(dispatch, appState, calypso, term, autoComp);
    } else {
      // clear search suggest
      dispatch(setHFSearchSuggestions());
      // in the case of a null search focus / cut
      dispatch(setHFSearchZeroState(true));
    }
  };
}

export function handleHFSearchReset() {
  return dispatch => {
    dispatch(setHFSearchSuggestionsActiveIndex(null));
    dispatch(setHFSearchTerm(''));
    dispatch(setHFSearchSuggestions());
    dispatch(setHFSearchZeroState(true));
  };
}

export function setFooterSubscribeSubmitted(isFooterSubscribeSubmitted) {
  return {
    type: SET_HF_FOOT_SUBSCRIBE_SUBMIT,
    isFooterSubscribeSubmitted
  };
}

export function setFooterSubscribeError(hasFooterSubscribeError) {
  return {
    type: SET_HF_FOOT_SUBSCRIBE_ERROR,
    hasFooterSubscribeError
  };
}

// HeaderFooter prevent double clicking on submit button
export function setHFFootSubscribing() {
  return {
    type: SET_HF_FOOT_SUBSCRIBE_SUBMITTING
  };
}

export function setFooterHistoryRecos(footerRecos) {
  return {
    type: SET_FOOTER_HISTORY_RECOS,
    footerRecos
  };
}

export function getFooterHistoryRecos(getRecos = recommendationsSearch) {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies, customer: isRecognizedCustomer } = state;
    const janusConfig = selectJanusConfig(state);

    if (isRecognizedCustomer) {
      return getRecos(janusConfig.url, cookies)
        .then(fetchErrorMiddleware)
        .then(data => {
          if (data['zap-hp-vh']) {
            const { recoName, recs } = data['zap-hp-vh'];
            // Create sensible link for href with reftag
            if (recs?.length) {
              recs.forEach((v, i) => {
                data['zap-hp-vh'].recs[i].link = `/product/${v.item_id}?ref=${recoName}`;
              });
              dispatch(setFooterHistoryRecos(data['zap-hp-vh']));
            }
          }
          trackOperation('GetFooterRecommendations', 'success');
        })
        .catch(err => {
          trackError('ERROR', 'Failed to fetch footer recently viewed', err);
          // INVALID_WIDGET_NAME means the user has no browsing history — not an operational failure
          const EXPECTED_EMPTY_RESPONSE_ERROR = 'INVALID_WIDGET_NAME';
          const isExpectedEmpty =
            err?.statusText?.includes?.(EXPECTED_EMPTY_RESPONSE_ERROR) || err?.message?.includes?.(EXPECTED_EMPTY_RESPONSE_ERROR);
          if (!isExpectedEmpty) {
            trackOperation('GetFooterRecommendations', 'failure');
          }
        });
    }
  };
}

export function setSignUpModal(isSignUpModalOpen) {
  trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT', 'close');
  return {
    type: SET_HF_SIGN_UP_MODAL,
    isSignUpModalOpen
  };
}

export function initSignUpModal() {
  return dispatch => {
    dispatch(setAndStoreCookie(NEWLY_REGISTERED_COOKIE, '', new Date(EXPIRE_IMMEDIATE_COOKIE_TIME)));
    dispatch(setSignUpModal(true));
  };
}

function subscribeEmail(mafiaConfig, credentials) {
  return subscribeToMarketingListZSub(mafiaConfig, { topic: 'GENERAL_NEWS' }, credentials)
    .then(fetchErrorMiddlewareMaybeJson)
    .then(() => {
      trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT', 'email');
    })
    .catch(err => {
      trackError('ERROR', 'Failed to subscribe to marketing email list via sign up modal', err);
    });
}

function subscribeRewards(mafiaConfig, akitaKey, cookies) {
  return enrollRewardsV2(mafiaConfig, akitaKey, cookies)
    .then(fetchErrorMiddleware)
    .then(() => {
      trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT', 'rewards');
    })
    .catch(err => {
      trackError('ERROR', 'Failed to subscribe to rewards via sign up modal', err);
    });
}

export function handleSignUpModalSubmit(e, subEmail = subscribeEmail, subRewards = subscribeRewards) {
  e.preventDefault();
  const emailChecked = e.target['email'] ? e.target['email'].checked : false;
  const rewardsChecked = e.target['rewards'] ? e.target['rewards'].checked : false;
  return (dispatch, getState) => {
    trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT');
    const state = getState();
    const {
      cookies,
      environmentConfig: { akitaKey }
    } = state;

    const mafiaConfig = selectMafiaConfig(state);

    dispatch(setSignUpModal(false));
    if (emailChecked) {
      subEmail(mafiaConfig, cookies);
    }
    if (rewardsChecked) {
      subRewards(mafiaConfig, akitaKey, cookies);
    }
  };
}

export function getProfile(profileCall = profile) {
  return (dispatch, getState) => {
    const {
      environmentConfig: {
        api: { opal }
      }
    } = getState();
    let cacheControl;
    return profileCall(opal)
      .then(res => {
        if (res.ok || res.status === 404) {
          // 404s are expected for unknown/new customers
          cacheControl = res.headers.get('Cache-Control');
          return res.json();
        } else {
          throw new Error('Failed fetch call');
        }
      })
      .then(json => {
        // Get expiration value from cache control header
        // Get number value from response which is in seconds. Default to one day(86400000).
        const cacheControlInt = cacheControl ? parseInt(cacheControl.replace(/\D/g, ''), 10) * 1000 : 86400000;
        const expiry = new Date(Date.now() + cacheControlInt);

        let data;
        if (json.error) {
          data = { searchProfile: { valid: false } };
        } else {
          data = { searchProfile: { ...json, valid: true } };
        }
        dispatch(setAndStoreCookie(OPAL_PROFILE_COOKIE, JSON.stringify(data), expiry));
      })
      .catch(err => {
        trackError('NON-FATAL', 'Search Profile Error:', err);
      });
  };
}

export function handleSearchKeyUp(e) {
  const {
    keyCode,
    target: { value }
  } = e;
  return (dispatch, getState) => {
    const {
      headerFooter: { suggestions, suggestionIndex, userSearchTerms, isZeroState }
    } = getState();
    const isNull = suggestionIndex === null;
    // If we're focused on search input

    let terms = suggestions;
    if (!value.trim() || isZeroState) {
      terms = userSearchTerms ?? [];
      if (!isZeroState) {
        dispatch(setHFSearchZeroState(true));
      }
      dispatch(setHFSearchSuggestions());
    }

    switch (keyCode) {
      case 40: // Key down
        e.preventDefault();
        if (suggestionIndex === terms.length - 1) {
          dispatch(setHFSearchSuggestionsActiveIndex(null));
        } else {
          dispatch(setHFSearchSuggestionsActiveIndex(!isNull ? suggestionIndex + 1 : 0));
        }
        break;
      case 38: // Key up
        e.preventDefault();
        if (suggestionIndex === 0) {
          dispatch(setHFSearchSuggestionsActiveIndex(null));
        } else {
          dispatch(setHFSearchSuggestionsActiveIndex(!isNull ? suggestionIndex - 1 : terms.length - 1));
        }
        break;
      case 27: // Escape
        dispatch(setHFSearchSuggestionsActiveIndex(null));
        break;
      default: // Any other key
        // left and right arrows
        if (keyCode !== 37 && keyCode !== 39) {
          if (!value.trim()) {
            dispatch(setHFSearchZeroState(true));
          } else {
            dispatch(setHFSearchZeroState(false));
            dispatch(handleHFSearchChange(e));
          }
        }
        break;
    }
  };
}

export const toggleMobileHeaderExpand = () => ({
  type: TOGGLE_MOBILE_HEADER_EXPAND
});

export const setFederatedLoginModalVisibility = (isVisible, options = {}) => {
  if (isVisible) {
    clearPromoBalanceSessionStorage();
  }

  return {
    type: SET_FEDERATED_LOGIN_MODAL_VISIBILITY,
    payload: {
      isFederatedLoginModalShowing: isVisible,
      returnTo: options.returnTo,
      redirectOnClose: options.redirectOnClose === '/' ? '{{applicationBaseUrl}}/' : options.redirectOnClose,
      sourcePageType: options.sourcePageType
    }
  };
};

export const setGlobalBannerData = data => ({
  type: SET_GLOBAL_BANNER_DATA,
  data
});

export const setDualGlobalBannersData = data => ({
  type: SET_DUAL_GLOBAL_BANNERS_DATA,
  data
});

export const cancelSegmentedGlobalBannerRefetch = () => (_dispatch, getState) => {
  const request = segmentedBannerRequests.get(getState);
  // A finished request still matches the stored banner, so only an in-flight one is dropped.
  if (request?.controller) {
    request.controller.abort();
    segmentedBannerRequests.set(getState, { needsRetry: true });
  }
};

export const refetchSegmentedGlobalBanner =
  ({ fetcher = fetch, preloadImages = preloadDualGlobalBannerImages, retryCancelledOnly = false } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    if (
      domain !== 'zappos' ||
      !isBannerPtcCatalogEnabled(state) ||
      state.products?.isLoading ||
      state.products?.searchError ||
      state.products?.isBlacklisted
    ) {
      return;
    }

    const previous = segmentedBannerRequests.get(getState);
    if (retryCancelledOnly && !previous?.needsRetry) {
      return;
    }

    const headers = buildBannerPtcHeaders(state);
    const fields = new URLSearchParams();
    const appendHeader = (fieldName, headerName, maxLength) => {
      if (headers[headerName]) {
        fields.set(fieldName, headers[headerName].slice(0, maxLength));
      }
    };
    appendHeader('platform', ZCS_PTC_PLATFORM_HEADER);
    // The route rejects longer referrers with a 400; it classifies only the host, so trimming keeps the same key.
    appendHeader('referrer', ZCS_PTC_REFERRER_HEADER, ZCS_PTC_REFERRER_MAX_LENGTH);
    appendHeader('businessUnit', ZCS_PTC_BUSINESS_UNIT_HEADER);
    appendHeader('category1', ZCS_PTC_CATEGORY1_HEADER);
    const body = fields.toString();

    if (previous?.body === body) {
      return;
    }
    previous?.controller?.abort();
    const request = { body, controller: new AbortController() };
    segmentedBannerRequests.set(getState, request);
    const isCurrentRequest = () => segmentedBannerRequests.get(getState) === request && isBannerPtcCatalogEnabled(getState());

    try {
      const response = await fetcher(SEGMENTED_GLOBAL_BANNER_ENDPOINT, {
        body,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        method: 'POST',
        signal: request.controller.signal
      });
      const data = await fetchErrorMiddlewareMaybeJson(response);

      if (!isCurrentRequest()) {
        return;
      }

      const banners = data?.banners;
      const slotData = banners?.slotData;
      if (!slotData) {
        // A 204 is not authoritative (a failed fetch, or no anonymous banner), so the same fields can retry.
        segmentedBannerRequests.delete(getState);
        return;
      }
      if (areDualGlobalBannersRenderEquivalent(banners, getState().headerFooter?.dualGlobalBanners?.data)) {
        return;
      }
      const hasVisibleBanner = slotData['banner-1'] || slotData['banner-2'];
      if (hasVisibleBanner) {
        await preloadImages(banners);
      }
      if (!isCurrentRequest() || areDualGlobalBannersRenderEquivalent(banners, getState().headerFooter?.dualGlobalBanners?.data)) {
        return;
      }
      dispatch(setDualGlobalBannersData(banners));
    } catch (error) {
      if (error?.name !== 'AbortError' && segmentedBannerRequests.get(getState) === request) {
        trackError('NON-FATAL', 'Could not refetch segmented global banner.', error);
        // Forget the failed fields so the next search change with the same fields retries.
        segmentedBannerRequests.delete(getState);
      }
    } finally {
      if (segmentedBannerRequests.get(getState) === request) {
        request.controller = undefined;
      }
    }
  };

export const setZAWHeaderData = data => ({
  type: SET_ZAW_CONTENT_SYMPHONY_HEADER_DATA,
  data
});

export const getZapposGlobalBannerData =
  (getData = getSymphonySlots) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      pageView: { pageType },
      cookies
    } = state;
    const zcsConfig = selectZcsConfig(state);
    const ptcHeaders = buildPtcBasicHeaders(state);

    return getData(zcsConfig, { pageLayout: 'GlobalBanner', pageName: PAGE_TYPE_BANNERS.get(pageType) }, cookies, ptcHeaders)
      .then(fetchErrorMiddleware)
      .then(data => {
        if (data) {
          dispatch(setGlobalBannerData(data));
        }
      })
      .catch(err => trackError('NON-FATAL', 'Global bottom banner:', err));
  };

export const dismissGlobalBanner = id => {
  const dismissedBanners = loadFromLocalStorage(DISMISSED_GLOBAL_BANNER_SESSION_STORAGE_KEY) || [];
  saveToLocalStorage(DISMISSED_GLOBAL_BANNER_SESSION_STORAGE_KEY, [...dismissedBanners, id]);
  return {
    type: DISMISS_GLOBAL_BANNER
  };
};

export const getZAWHeaderData =
  (getData = getSymphonySlots) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      environmentConfig: {
        api: { zcs }
      },
      cookies
    } = state;
    const ptcHeaders = buildPtcBasicHeaders(state);
    return getData(zcs, { pageLayout: 'KrsOne', pageName: 'zawHeader' }, cookies, ptcHeaders)
      .then(fetchErrorMiddleware)
      .then(data => {
        if (data) {
          dispatch(setZAWHeaderData(data));
        }
      })
      .catch(err => trackError('NON-FATAL', 'ZAW Data fetch:', err));
  };
